import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Helper function to extract header text from JSX or string
const extractHeaderText = (header) => {
  if (typeof header === "string") {
    return header;
  }
  if (typeof header === "function") {
    try {
      const result = header();
      if (typeof result === "string") {
        return result;
      }
      if (result?.props) {
        // Handle JSX elements - recursively look for text in children
        const extractTextFromProps = (props) => {
          // Direct string children
          if (typeof props.children === "string") {
            return props.children;
          }

          // Array of children - look for span or text elements
          if (Array.isArray(props.children)) {
            for (const child of props.children) {
              if (typeof child === "string") {
                return child;
              }
              // Check if it's a React element (has type and props)
              if (child?.props) {
                // If it's a span or similar element, get its children
                if (child.type === "span" || typeof child.type === "string") {
                  const text = extractTextFromProps(child.props);
                  if (text) return text;
                }
                // Recursively check nested children
                const nestedText = extractTextFromProps(child.props);
                if (nestedText) return nestedText;
              }
            }
          }

          // Single child element
          if (props.children?.props) {
            const text = extractTextFromProps(props.children.props);
            if (text) return text;
          }

          // Direct children as object
          if (
            props.children &&
            typeof props.children === "object" &&
            !Array.isArray(props.children)
          ) {
            if (props.children.props) {
              const text = extractTextFromProps(props.children.props);
              if (text) return text;
            }
          }

          return null;
        };
        const text = extractTextFromProps(result.props);
        if (text) return text.trim();
      }
    } catch (e) {
      console.warn("Error extracting header text:", e);
    }
  }
  return "";
};

// Helper function to extract cell value from row data
const extractCellValue = (col, row) => {
  // Skip columns that shouldn't be exported
  const headerText = extractHeaderText(col.Header);
  if (
    col.accessor === "checkbox" ||
    headerText === "ACTIONS" ||
    !col.accessor
  ) {
    return null; // Signal to skip this column
  }

  // If column has a custom Cell renderer, we need to extract the value manually
  // based on the accessor and row data
  let value = row[col.accessor];

  // Handle special cases based on accessor
  if (col.accessor === "delivery_address") {
    // Delivery address uses to_address from the row
    value = row.to_address || "-";
  } else if (col.accessor === "sms_flag") {
    // SMS flag needs special handling to match SMSDeliveryBadge logic
    const normalizedValue = String(value || "").toLowerCase();
    if (normalizedValue === "yes") {
      value = "Transcription";
    } else if (normalizedValue === "notification") {
      value = "Notification";
    } else {
      value = "Disabled";
    }
  }

  // Handle date fields
  if (col.accessor && col.accessor.toLowerCase().includes("date")) {
    value = value ? " " + value : "";
  }

  // Handle boolean values
  if (typeof value === "boolean") {
    value = value ? "Yes" : "No";
  }

  // Handle null/undefined
  if (value === null || value === undefined) {
    value = "-";
  }

  // Handle objects (shouldn't happen with proper extraction, but just in case)
  if (typeof value === "object" && value !== null) {
    value = JSON.stringify(value);
  }

  return value;
};

export const convertToCSV = async (data, columns) => {
  // Filter out columns that shouldn't be exported (checkbox, actions)
  const exportableColumns = columns.filter((col) => {
    if (!col.accessor) return false;
    if (col.accessor === "checkbox") return false;
    const headerText = extractHeaderText(col.Header);
    if (headerText === "ACTIONS") return false;
    return true;
  });

  // Extract headers
  const header = exportableColumns
    .map((col) => {
      const headerText = extractHeaderText(col.Header);
      return '"' + headerText + '"';
    })
    .join(",");

  // Extract rows
  const rows = data.map((row) =>
    exportableColumns
      .map((col) => {
        let value = extractCellValue(col, row);
        if (value === null) {
          return '""'; // Skip column
        }
        // Escape quotes in strings
        if (typeof value === "string") {
          value = value.replace(/"/g, '""');
        }
        return '"' + value + '"';
      })
      .join(","),
  );

  return [header, ...rows].join("\r\n");
};

export const exportLogsToPDF = (
  data,
  columns,
  filename,
  title = "PBXScribe Transcription Logs",
) => {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });

  // Filter out columns that shouldn't be exported (checkbox, actions)
  const exportableColumns = columns.filter((col) => {
    if (!col.accessor) return false;
    if (col.accessor === "checkbox") return false;
    const headerText = extractHeaderText(col.Header);
    if (headerText === "ACTIONS") return false;
    return true;
  });

  // Extract headers
  const headers = exportableColumns.map((col) => {
    return extractHeaderText(col.Header);
  });

  // Extract rows
  const rows = data.map((row) =>
    exportableColumns.map((col) => {
      const value = extractCellValue(col, row);
      return value === null ? "" : String(value);
    }),
  );

  doc.setFontSize(18);
  doc.text(title, 40, 40);

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 60,
    theme: "striped",
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontSize: 11,
      halign: "center",
    },
    bodyStyles: {
      fontSize: 10,
      valign: "middle",
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    styles: {
      cellPadding: 4,
      overflow: "linebreak",
      minCellHeight: 18,
    },
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(9);
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 100,
        doc.internal.pageSize.getHeight() - 10,
      );
    },
  });
  doc.save(`${filename}.pdf`);
};

export const downloadFile = (filename, content, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const normalizeCellText = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim();

const shouldIncludeColumnHeader = (header) => {
  const normalized = normalizeCellText(header).toLowerCase();
  if (!normalized) return false;
  if (normalized === "actions" || normalized === "action") return false;
  return true;
};

export const getRenderedTableSnapshot = (tableElement) => {
  if (!tableElement) return null;

  const headerCells = Array.from(tableElement.querySelectorAll("thead th"));
  const includedColumnIndexes = [];
  const headers = [];

  headerCells.forEach((th, index) => {
    const headerClone = th.cloneNode(true);
    headerClone
      .querySelectorAll("svg, i, input, button, [role='button'], .fa, .svg-inline--fa")
      .forEach((el) => el.remove());

    const headerText = normalizeCellText(headerClone.textContent);
    if (shouldIncludeColumnHeader(headerText)) {
      includedColumnIndexes.push(index);
      headers.push(headerText);
    }
  });

  const bodyRows = Array.from(tableElement.querySelectorAll("tbody tr"));
  const rows = bodyRows.map((tr) => {
    const cells = Array.from(tr.querySelectorAll("td"));
    return includedColumnIndexes.map((colIndex) => {
      const cell = cells[colIndex];
      if (!cell) return "";

      const cellClone = cell.cloneNode(true);
      cellClone
        .querySelectorAll("svg, i, input, button, [role='button'], .fa, .svg-inline--fa")
        .forEach((el) => el.remove());

      return normalizeCellText(cellClone.textContent);
    });
  });

  return { headers, rows };
};

export const convertTableSnapshotToCSV = (snapshot) => {
  if (!snapshot || !snapshot.headers?.length) return "";

  const escapeCell = (value) =>
    '"' + String(value || "").replace(/"/g, '""') + '"';

  const headerLine = snapshot.headers.map(escapeCell).join(",");
  const rowLines = (snapshot.rows || []).map((row) =>
    row.map(escapeCell).join(","),
  );

  return [headerLine, ...rowLines].join("\r\n");
};

export const exportTableSnapshotToPDF = (
  snapshot,
  filename = "export",
  title = "PBXScribe Export",
) => {
  if (!snapshot || !snapshot.headers?.length) return;

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });

  doc.setFontSize(18);
  doc.text(title, 40, 40);

  autoTable(doc, {
    head: [snapshot.headers],
    body: snapshot.rows || [],
    startY: 60,
    theme: "striped",
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontSize: 11,
      halign: "center",
    },
    bodyStyles: {
      fontSize: 10,
      valign: "middle",
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    styles: {
      cellPadding: 4,
      overflow: "linebreak",
      minCellHeight: 18,
    },
    didDrawPage: () => {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(9);
      doc.text(
        `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 100,
        doc.internal.pageSize.getHeight() - 10,
      );
    },
  });

  doc.save(`${filename}.pdf`);
};
